// The named export: pino is CommonJS, and under NodeNext its default import is the whole module object, not the function.
import { pino } from 'pino';
// Create the logger instance

const getLogLevel = () => {
  if(typeof process !== 'undefined' && process?.env) {
    return process.env['LOG_LEVEL'] || 'info';
  }
  return 'info';
}

/**
 * pino-pretty, in its worker thread — unless NODE_ENV is `test`, where the records stay JSON lines. `messageKey` is the
 * key the logger's records carry their message under: pino-pretty prints that key on the record's own line, and reads
 * pino's default (`msg`) unless told — a record that carries `message` then printed an empty line, the message beneath
 * it as a quoted field.
 */
const getTransport = (messageKey?: string) => {
  if(typeof process !== 'undefined' && process?.env?.['NODE_ENV'] === 'test') {
    return undefined; // No transport in test environment
  }
  return {
    target: 'pino-pretty',
    options: {
      colorize: true,
      translateTime: true,
      ...(messageKey === undefined ? {} : { messageKey }),
    },
  };
}

/** The key a createLogger record carries its message under. */
const MESSAGE_KEY = 'message';

/** The level methods whose message gets the prefix. */
const LOG_METHODS = new Set(['fatal', 'error', 'warn', 'info', 'debug', 'trace']);

/**
 * Create a prefixed pino logger.
 *
 * The prefix is applied by wrapping the level methods, NOT by pino's
 * `hooks.logMethod`. `hooks` is a node-build-only option: every bundled
 * `dist` here resolves pino's BROWSER build (asObject), which ignores it and
 * silently dropped the prefix from every built package — the prefix survived
 * only when running from source. That silence cost real time: three e2e
 * globalSetups wait for the Starter to announce itself with a "STARTER: "
 * prefixed line and hung until timeout instead, and the demo-e2e worker fixture
 * keys its error detection off the same prefix.
 */
export const createLogger = (prefix: string, level=getLogLevel()) => {
  return withPrefix(pino({
    level,
    base: null, // Remove default properties like PID
    messageKey: MESSAGE_KEY,
    browser: {
      asObject: true,
    },
    transport: getTransport(MESSAGE_KEY),
  }), prefix);
};

/**
 * Tag every string message of an EXISTING logger with `prefix`.
 *
 * For a component handed a logger by whoever started it: it keeps the caller's
 * level and transport while still saying which component spoke. Without this a
 * component that accepts an injected logger loses its identity in the output
 * entirely — the Starter's lines read `CLI: ...`, and anything watching for
 * `STARTER: ...` (the e2e globalSetups wait on exactly that line to know the
 * servers are up) waits forever.
 */
export const withPrefix = <T extends object>(logger: T, prefix: string): T => {
  return new Proxy(logger, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof value !== 'function' || typeof prop !== 'string' || !LOG_METHODS.has(prop)) {
        return value;
      }
      // Mirrors what the old hook did: prefix a leading string message and
      // leave the `(obj, msg)` form alone.
      return (...args: any[]) => {
        if (typeof args[0] === 'string') args[0] = `${prefix} ${args[0]}`;
        return value.apply(target, args);
      };
    },
  });
};

/** A log method: pino's call forms — `(msg, ...args)` and `(obj, msg?, ...args)`. */
export type LogFn = (...args: any[]) => void;

/**
 * The logger shape every framework server class uses. A pino `Logger`
 * satisfies it, so does anything with these six methods.
 */
export type GottiLogger = {
  fatal: LogFn;
  error: LogFn;
  warn: LogFn;
  info: LogFn;
  debug: LogFn;
  trace: LogFn;
};

/**
 * Who is logging. Framework servers pass `kind` + `serverId` (+ `areaId` /
 * `gameId` for an area); a game System's logger passes `kind: 'system'` (its
 * name is in the prefix), the Deployer's `kind: 'deployer'`. A factory turns
 * them into structured fields instead of re-parsing the human prefix.
 */
export type LoggerBindings = {
  kind?: 'master' | 'gate' | 'connector' | 'area' | 'starter' | 'webClient' | 'server' | 'deployer' | 'system' | (string & {});
  serverId?: string | number;
  areaId?: string | number;
  gameId?: string | number;
};

/** Builds the logger for one framework component. `prefix` is the default logger's human prefix. */
export type LoggerFactory = (ctx: { prefix: string; bindings: LoggerBindings; level: string }) => GottiLogger;

let loggerFactory: LoggerFactory | undefined;

/**
 * Route every framework logger created from now on through `factory` — e.g. an
 * embedding host that wants structured `{ kind, serverId, areaId }` records
 * instead of prefixed pretty-printed lines. Set it ONCE per process, before
 * the servers are constructed (loggers are created with their component); a
 * second call throws rather than silently splitting output between two sinks.
 * Returns a function that restores the default (after which it may be set
 * again).
 *
 * Unset, `getLogger` is exactly `createLogger(prefix, level)`.
 */
export const setLoggerFactory = (factory: LoggerFactory): (() => void) => {
  if (typeof factory !== 'function') throw new Error('setLoggerFactory needs a function');
  if (loggerFactory) {
    throw new Error('A gotti logger factory is already set for this process; call the restore function setLoggerFactory returned before setting another.');
  }
  loggerFactory = factory;
  return () => {
    if (loggerFactory === factory) loggerFactory = undefined;
  };
};

/**
 * The logger for one framework component: the installed factory's, or the
 * default prefixed pino logger (`createLogger(prefix, level)`).
 */
export const getLogger = (prefix: string, bindings: LoggerBindings = {}, level: string = getLogLevel()): GottiLogger => {
  if (loggerFactory) return loggerFactory({ prefix, bindings, level });
  return createLogger(prefix, level);
};

/**
 * A logger resolved on first use — for module-scope loggers, which are created
 * at import time, before an embedder had the chance to `setLoggerFactory`.
 */
export const lazyLogger = (prefix: string, bindings: LoggerBindings = {}): GottiLogger => {
  let resolved: GottiLogger | undefined;
  const at = (method: keyof GottiLogger): LogFn => (...args) => {
    resolved ??= getLogger(prefix, bindings);
    resolved[method](...args);
  };
  return { fatal: at('fatal'), error: at('error'), warn: at('warn'), info: at('info'), debug: at('debug'), trace: at('trace') };
};

const logger = pino({
  level: getLogLevel(),
  transport: getTransport(),
});

export default logger;
